import type { FC } from 'react';
import { useState, useEffect } from 'react';
import {
  ProtoActiveCard,
  ProtoBottomRow,
  ProtoCardTitle,
  ProtoTopRow,
  ProtoMeta,
  ProtoPrimaryButton,
  ProtoAddButton,
} from '../TrainingCard';
import Play from '../../../../assets/Play.svg';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import { useStartVoluntaryTrainingQuery } from '../../../../apiHooks/useStartVoluntaryTrainingQuery';
import { useStartVoluntaryTrainingMutation } from '../../../../apiHooks/useStartVoluntaryTrainingMutation';
import { Spinner } from '../../../Spinner/Spinner';
import type {
  EnrolledCampaignListResponse,
  VoluntaryItem,
  VoluntaryTrainingListResponse,
} from '../../../../apiHooks/useCampaignsQuery';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { PlusCircledIcon } from '@radix-ui/react-icons';
import { Coin } from '../../../Props';
import { Tooltip } from '../../../Tooltip';
import styled from 'styled-components';

type VoluntaryTrainingCardProps = {
  campaign: VoluntaryItem;
  onComplete: () => void;
};

export const VoluntaryTrainingCard: FC<VoluntaryTrainingCardProps> = ({
  campaign,
  onComplete,
}: VoluntaryTrainingCardProps) => {
  const { id, name } = campaign;
  const isLoading = (campaign as VoluntaryTrainingListResponse).isLoading;
  const isActive = (campaign as VoluntaryTrainingListResponse).isLoading === undefined;
  const [shouldFetchTrainingStatus, setShouldFetchTrainingStatus] = useState(false);
  const [isTrainingPending, setIsTrainingPending] = useState(isLoading);
  const { modulesProgress, modulesTotal } = campaign as EnrolledCampaignListResponse;

  const { data: trainingData } = useStartVoluntaryTrainingQuery({
    id,
    options: {
      enabled: shouldFetchTrainingStatus,
      refetchInterval: shouldFetchTrainingStatus ? 5000 : false,
    },
  });

  const { mutate } = useStartVoluntaryTrainingMutation();

  useEffect(() => {
    if (trainingData?.isLoading === false) {
      setShouldFetchTrainingStatus(false);
      setIsTrainingPending(false);
      onComplete();
    } else if (trainingData?.isLoading === true) {
      setIsTrainingPending(true);
    }
  }, [trainingData, onComplete]);

  useEffect(() => {
    if (isLoading) {
      setShouldFetchTrainingStatus(true);
      setIsTrainingPending(true);
    }
  }, [isLoading]);

  const handleStartTraining = () => {
    if ((campaign as VoluntaryTrainingListResponse).isLoading === undefined) {
      if ((campaign as EnrolledCampaignListResponse).campaignUrl) {
        window.open((campaign as EnrolledCampaignListResponse).campaignUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      mutate(
        { campaignId: id },
        {
          onSuccess: () => {
            setShouldFetchTrainingStatus(true);
            setIsTrainingPending(true);
          },
        },
      );
    }
  };

  return (
    <ProtoActiveCard>
      <ProtoTopRow>
        <ProtoCardTitle>{name}</ProtoCardTitle>
        {!isActive && <StyledCoin value={(campaign as VoluntaryTrainingListResponse).points} />}
      </ProtoTopRow>
      <ProtoBottomRow>
        <ProtoMeta>
          {isActive ? (
            <>
              <Icon icon={IconType.CLIPBOARD_CHECK} width={12} height={12} />
              {modulesProgress} / {modulesTotal}
            </>
          ) : (
            <Icon icon={IconType.GOAL_FLAG} width={12} height={12} className="rtl-flip" />
          )}
        </ProtoMeta>
        {isActive ? (
          <ProtoPrimaryButton onClick={handleStartTraining} disabled={isTrainingPending}>
            <Play className="rtl-flip" />
            <NamedContentEntryText container="span" refKey="yourTraining" subKey="startNow" />
          </ProtoPrimaryButton>
        ) : isTrainingPending ? (
          <Spinner size={'12px'} color={'blue'} />
        ) : (
          <Tooltip
            placement="left"
            content={<AddVoluntaryTrainingTooltip>Add to voluntary training</AddVoluntaryTrainingTooltip>}
          >
            <ProtoAddButton onClick={handleStartTraining} aria-label="Add to voluntary training">
              <PlusCircledIcon width={18} height={18} />
            </ProtoAddButton>
          </Tooltip>
        )}
      </ProtoBottomRow>
    </ProtoActiveCard>
  );
};

const AddVoluntaryTrainingTooltip = styled.div`
  width: max-content;
  text-align: center;
  padding: 8px;
  border-radius: 4px;
`;

const StyledCoin = styled(Coin)`
  padding: 2px 4px;
  & > span {
    font-size: 11px;
    font-weight: 400;
  }

  & > svg {
    width: 16px;
    height: 16px;
  }
`;
